"use client";

import { type FormEvent, useMemo, useState } from "react";
import {
  upsertDecisionJourney,
  type AccountChartSubject,
  type AccountReading,
  type DecisionJourneyGoal,
  type DecisionJourneyType,
} from "./account-api";

const GOALS: Record<DecisionJourneyType, Array<{ value: DecisionJourneyGoal; label: string }>> = {
  career: [
    { value: "job_search", label: "Find the right next role" },
    { value: "promotion", label: "Prepare for a promotion" },
    { value: "business_launch", label: "Launch a business" },
    { value: "career_change", label: "Change career direction" },
  ],
  marriage: [
    { value: "partner_search", label: "Search for a life partner" },
    { value: "compatibility_review", label: "Review compatibility" },
    { value: "engagement_planning", label: "Plan an engagement" },
    { value: "marriage_preparation", label: "Prepare for marriage" },
  ],
};

export function AccountDecisionJourneyForm({
  chartSubjects,
  readings,
  token,
  onSaved,
}: {
  chartSubjects: AccountChartSubject[];
  readings: AccountReading[];
  token: string;
  onSaved: () => Promise<void>;
}) {
  const [journeyType, setJourneyType] = useState<DecisionJourneyType>("career");
  const [goalKey, setGoalKey] = useState<DecisionJourneyGoal>(GOALS.career[0].value);
  const [readingId, setReadingId] = useState(readings[0]?.reading_id ?? "");
  const [subjectId, setSubjectId] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const partnerCharts = useMemo(
    () =>
      chartSubjects.filter((subject) =>
        ["partner", "spouse"].includes(subject.relationship_to_owner)
      ),
    [chartSubjects]
  );

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!readingId || busy) return;
    setBusy(true);
    setError("");
    try {
      await upsertDecisionJourney(token, {
        readingId,
        journeyType,
        goalKey,
        targetDate,
        subjectId: journeyType === "marriage" ? subjectId : undefined,
        idempotencyKey: crypto.randomUUID(),
      });
      await onSaved();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not save this journey.");
    } finally {
      setBusy(false);
    }
  }

  function selectType(value: DecisionJourneyType) {
    setJourneyType(value);
    setGoalKey(GOALS[value][0].value);
    setSubjectId("");
  }

  return (
    <form className="mt-4 grid gap-4" onSubmit={submit}>
      <div className="grid grid-cols-2 rounded-lg border border-[var(--color-border)] p-1">
        {(["career", "marriage"] as const).map((type) => (
          <button
            key={type}
            type="button"
            aria-pressed={journeyType === type}
            onClick={() => selectType(type)}
            className={`rounded-md px-3 py-2 text-sm font-semibold ${
              journeyType === type
                ? "bg-[var(--color-primary)] text-white"
                : "text-[var(--color-text-sub)]"
            }`}
          >
            {type === "career" ? "Career" : "Marriage"}
          </button>
        ))}
      </div>
      <JourneySelect
        id="decision-journey-goal"
        label="What is the decision?"
        value={goalKey}
        onChange={(value) => setGoalKey(value as DecisionJourneyGoal)}
        options={GOALS[journeyType]}
      />
      <JourneySelect
        id="decision-journey-reading"
        label="Use this saved kundali"
        value={readingId}
        onChange={setReadingId}
        options={readings.map((reading) => ({ value: reading.reading_id, label: reading.title }))}
      />
      {journeyType === "marriage" && partnerCharts.length > 0 ? (
        <JourneySelect
          id="decision-journey-partner"
          label="Partner chart (optional)"
          value={subjectId}
          onChange={setSubjectId}
          options={[
            { value: "", label: "No partner chart yet" },
            ...partnerCharts.map((subject) => ({
              value: subject.subject_id,
              label: subject.display_name,
            })),
          ]}
        />
      ) : null}
      <label className="text-sm font-semibold text-[var(--color-text-main)]">
        Target date (optional)
        <input
          type="date"
          value={targetDate}
          onChange={(event) => setTargetDate(event.target.value)}
          className="mt-1.5 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-raised)] px-3 py-2 text-sm"
        />
      </label>
      {error ? <p className="text-sm text-[var(--color-error-text)]">{error}</p> : null}
      <button
        type="submit"
        disabled={busy || !readingId}
        className="rounded-md bg-[var(--color-accent)] px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"
      >
        {busy ? "Saving journey..." : "Save decision journey"}
      </button>
    </form>
  );
}

function JourneySelect({
  id,
  label,
  onChange,
  options,
  value,
}: {
  id: string;
  label: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  value: string;
}) {
  return (
    <label htmlFor={id} className="text-sm font-semibold text-[var(--color-text-main)]">
      {label}
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1.5 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-raised)] px-3 py-2 text-sm"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}
